import { useEffect, useState } from "react";
import Alert from "react-bootstrap/Alert";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Nav from "react-bootstrap/Nav";
import Row from "react-bootstrap/Row";
import Spinner from "react-bootstrap/Spinner";
import Tab from "react-bootstrap/Tab";
import { useSearchParams } from "react-router-dom";
import { fetchCatalog } from "../api.js";
import CatalogItemCard from "../components/CatalogItemCard.jsx";

// Render a `\n`-delimited blurb as one <p> per line.
function Paragraphs({ text, className }) {
  if (!text) return null;
  const lines = text.split("\n").filter((l) => l.trim());
  return lines.map((l, i) => (
    <p key={i} className={`${className} ${i < lines.length - 1 ? "mb-2" : "mb-0"}`}>
      {l}
    </p>
  ));
}

// Group a tab's items by subCategory (insertion order preserved from the
// server's sku sort). Items with no subCategory fall under "Other".
function groupBySubCategory(items) {
  const map = new Map();
  for (const item of items) {
    const key = item.subCategory || "Other";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(item);
  }
  return map;
}

// Sub-category keys sorted alphabetically, with "Other" forced last.
function sortedSubKeys(subMap) {
  return [...subMap.keys()].sort((a, b) => {
    if (a === "Other") return 1;
    if (b === "Other") return -1;
    return a.localeCompare(b);
  });
}

function ItemGrid({ items }) {
  const list = items ?? [];
  // A category/sub-category with a single item gets a full-width horizontal
  // card so it fills the row instead of leaving a 1/3-width card adrift.
  if (list.length === 1) {
    return (
      <Row className="g-4">
        <Col xs={12}>
          <CatalogItemCard item={list[0]} horizontal />
        </Col>
      </Row>
    );
  }
  return (
    <Row className="g-4">
      {list.map((item) => (
        <Col key={item.sku} xs={12} sm={6} lg={4}>
          <CatalogItemCard item={item} />
        </Col>
      ))}
    </Row>
  );
}

// Flat grid when a category has a single sub-category; otherwise render the
// sub-categories as their own (alphabetical) sub-tabs.
function CategoryPane({ items, requestedSub }) {
  const subMap = groupBySubCategory(items ?? []);
  const subKeys = sortedSubKeys(subMap);
  const [activeSub, setActiveSub] = useState(
    requestedSub && subKeys.includes(requestedSub) ? requestedSub : subKeys[0] ?? ""
  );

  if (subKeys.length < 2) {
    return <ItemGrid items={items ?? []} />;
  }

  const current = subKeys.includes(activeSub) ? activeSub : subKeys[0];

  return (
    <Tab.Container activeKey={current} onSelect={(k) => k && setActiveSub(k)}>
      <div className="pmp-catalog-subtabs-wrap mb-4">
        <Nav variant="pills" className="pmp-catalog-subtabs-nav flex-nowrap gap-2">
          {subKeys.map((key) => (
            <Nav.Item key={key}>
              <Nav.Link
                eventKey={key}
                className="pmp-catalog-subtab-link text-nowrap px-3 py-2"
              >
                {key}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
      </div>
      <Tab.Content>
        {subKeys.map((key) => (
          <Tab.Pane key={key} eventKey={key}>
            <ItemGrid items={subMap.get(key)} />
          </Tab.Pane>
        ))}
      </Tab.Content>
    </Tab.Container>
  );
}

export default function CatalogPage() {
  const [groups, setGroups] = useState([]);
  const [activeKey, setActiveKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchCatalog();
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setGroups(list);
        if (list.length) {
          // Preselect the tab named by ?category= when it matches a group.
          const requested = searchParams.get("category");
          const match = requested && list.find((g) => g.category === requested);
          setActiveKey(match ? match.category : list[0].category);
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <section className="py-5 pmp-section-alt">
        <Container className="py-5 d-flex justify-content-center">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading</span>
          </Spinner>
        </Container>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-5 pmp-section-alt">
        <Container className="py-4">
          <Alert variant="danger" className="mb-0 shadow-sm">
            {error}
          </Alert>
        </Container>
      </section>
    );
  }

  if (!groups.length) {
    return (
      <section className="py-5 pmp-section-alt">
        <Container className="py-5">
          <p className="pmp-kicker mb-2">Product catalog</p>
          <h1 className="h2 mb-3">Browse by category</h1>
          <p className="text-secondary mb-0">No catalog items yet.</p>
        </Container>
      </section>
    );
  }

  const requestedCategory = searchParams.get("category");
  const requestedSub = searchParams.get("sub");

  return (
    <section className="py-5 pmp-section-alt">
      <Container>
        <p className="pmp-kicker mb-2">Product catalog</p>
        <h1 className="h2 mb-4">Browse by category</h1>
        <Tab.Container activeKey={activeKey} onSelect={(k) => k && setActiveKey(k)}>
          <div className="pmp-catalog-tabs-wrap mb-4">
            <Nav
              variant="pills"
              className="pmp-catalog-tabs-nav flex-nowrap gap-2"
            >
              {groups.map((g) => (
                <Nav.Item key={g.category}>
                  <Nav.Link eventKey={g.category} className="pmp-catalog-tab-link text-nowrap px-3 py-2">
                    {g.category}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
          </div>
          <Tab.Content>
            {groups.map((g) => (
              <Tab.Pane key={g.category} eventKey={g.category}>
                {g.summary || g.details ? (
                  <div className="pmp-catalog-pane-intro mb-4">
                    <Paragraphs text={g.summary} className="pmp-catalog-pane-summary" />
                    <Paragraphs text={g.details} className="pmp-catalog-pane-details" />
                  </div>
                ) : null}
                <CategoryPane
                  items={g.items}
                  requestedSub={g.category === requestedCategory ? requestedSub : undefined}
                />
              </Tab.Pane>
            ))}
          </Tab.Content>
        </Tab.Container>
      </Container>
    </section>
  );
}
